import { isCohortSession, joinDoor, type Event } from '@lb/domain'

/**
 * What an event card offers — one decision, in one place.
 *
 * It was written three times by 13 Oct: the list's row, the event page's rail,
 * and Home's « À venir ». Three copies of a branch that decides whether
 * somebody can give away a place they paid for is how `bought` ends up handled
 * on two screens out of three — which is exactly what Home was doing, offering
 * « Je ne pourrai pas » on a bought ticket.
 *
 *   session     the member's own cohort session: the link, never an RSVP
 *   join        the server has handed the join link over
 *   ticket      this place was PAID FOR
 *   soldOut     no place left to sell, and none held by this member
 *   buy         locked and priced — a place to buy
 *   locked      locked with no price — the membership is the way in
 *   rsvp        the ordinary toggle
 */
export type EventAction = 'session' | 'join' | 'ticket' | 'soldOut' | 'buy' | 'locked' | 'rsvp'

export function eventAction(
  event: Pick<
    Event,
    | 'cohorts'
    | 'session'
    | 'joinUrl'
    | 'hasJoinLink'
    | 'joinClosesAt'
    | 'myRsvp'
    | 'locked'
    | 'priceCents'
    | 'soldOut'
    | 'rsvpCapacity'
    | 'rsvpCount'
  >,
  options: { bought?: boolean; locked?: boolean } = {},
  now: Date = new Date(),
): EventAction {
  if (isCohortSession(event)) return 'session'
  if (joinDoor(event, now) === 'open') return 'join'
  /*
   * A ticket, and **no way to give it up by accident**. The RSVP toggle is one
   * tap from « Je ne pourrai pas », which here hands back a place that was
   * paid for and is not refundable — a support conversation, not a button.
   */
  if (options.bought) return 'ticket'

  /*
   * `locked` is the SERVER's answer and the caller passes what it read.
   * Never recomputed from `premium` and an entitlement: a single-purchase
   * buyer is `entitled: false` and owns one row, so a derived lock covers the
   * thing they paid for. That defect is live on mobile today.
   */
  const locked = options.locked ?? event.locked === true

  /*
   * « Complet » counts confirmed places **plus unexpired holds** — a place is
   * held the moment a payment page is minted — which a count here cannot see.
   * The count stays as the fallback for a server predating `soldOut`.
   */
  const full =
    (event.soldOut || (event.rsvpCapacity !== null && event.rsvpCount >= event.rsvpCapacity)) &&
    !event.myRsvp

  if (locked) return event.priceCents != null ? (full ? 'soldOut' : 'buy') : 'locked'
  return full ? 'soldOut' : 'rsvp'
}
